/**
 * CPF e CNPJ (inclusive o CNPJ alfanumérico da IN RFB 2.229/2024): normalização, máscara e
 * dígitos verificadores. Mesma regra de fiscal.valid_cpf / fiscal.valid_cnpj e finance.valid_doc.
 */

export type DocKind = "cpf" | "cnpj";

/** Mantém só 0-9 e A-Z (maiúsculas). */
export function normalizeDoc(raw: string): string {
  return raw.toUpperCase().replace(/[^0-9A-Z]/g, "");
}

export function docKind(normalized: string): DocKind | null {
  if (/^\d{11}$/.test(normalized)) return "cpf";
  if (/^[0-9A-Z]{12}\d{2}$/.test(normalized)) return "cnpj";
  return null;
}

export function isValidCpf(raw: string): boolean {
  const d = normalizeDoc(raw);
  if (!/^\d{11}$/.test(d) || /^(\d)\1{10}$/.test(d)) return false;
  const calc = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === Number(d[9]) && calc(10) === Number(d[10]);
}

const cnpjValue = (ch: string) => ch.charCodeAt(0) - 48;

export function isValidCnpj(raw: string): boolean {
  const d = normalizeDoc(raw);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(d)) return false;
  if (/^(\d)\1{13}$/.test(d)) return false;
  const dv = (len: number) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < len; i++) sum += cnpjValue(d[i]!) * weights[i]!;
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(12) === Number(d[12]) && dv(13) === Number(d[13]);
}

export function isValidDoc(raw: string, kind?: DocKind): boolean {
  const d = normalizeDoc(raw);
  const k = kind ?? docKind(d);
  if (k === "cpf") return isValidCpf(d);
  if (k === "cnpj") return isValidCnpj(d);
  return false;
}

/** Máscara progressiva: CPF 000.000.000-00; CNPJ 00.000.000/0000-00 (aceita letras nos 12 primeiros). */
export function maskDoc(raw: string, kind?: DocKind): string {
  const d = normalizeDoc(raw);
  const k: DocKind = kind ?? (d.length <= 11 && /^\d*$/.test(d) ? "cpf" : "cnpj");
  if (k === "cpf") {
    const v = d.replace(/\D/g, "").slice(0, 11);
    return v
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
  }
  const v = d.slice(0, 14);
  let out = "";
  for (let i = 0; i < v.length; i++) {
    if (i === 2 || i === 5) out += ".";
    if (i === 8) out += "/";
    if (i === 12) out += "-";
    out += v[i];
  }
  return out;
}

/** Exibe documento já gravado (só dígitos/letras) com máscara; vazio → "". */
export function formatDoc(value: string | null | undefined): string {
  if (!value) return "";
  const d = normalizeDoc(value);
  const k = docKind(d);
  return k ? maskDoc(d, k) : value;
}

export function docError(raw: string, kind?: DocKind): string | null {
  const d = normalizeDoc(raw);
  if (d === "") return null;
  if (kind === "cpf" && d.length !== 11) return "CPF precisa de 11 dígitos.";
  if (kind === "cnpj" && d.length !== 14) return "CNPJ precisa de 14 caracteres.";
  const k = kind ?? docKind(d);
  if (!k) return "Informe um CPF (11 dígitos) ou CNPJ (14 caracteres).";
  if (!isValidDoc(d, k)) return k === "cpf" ? "CPF inválido: confira os dígitos." : "CNPJ inválido: confira os dígitos.";
  return null;
}
